import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import {
  FaChevronLeft,
  FaChevronRight,
  FaEnvelope,
  FaFacebook,
  FaHome,
  FaInfoCircle,
  FaPhone,
  FaTools,
  FaWhatsapp,
  FaImages,
  FaShoppingBag,
} from "react-icons/fa";
import logoLight from "../../assets/images/logoLight.svg";
import { NAV_LINKS } from "../../constants/navigation";
import { ROUTES } from "../../constants/routes";
import { SOCIAL_LINKS } from "../../constants/site";
import { scrollToTop } from "../../utils/scroll";
import { lockBodyScroll, unlockBodyScroll } from "../../utils/scrollLock";
import { isRtlLanguage } from "../../utils/i18n";
import "./MobileSidebar.css";

const NAV_ICONS = {
  [ROUTES.home]: FaHome,
  [ROUTES.about]: FaInfoCircle,
  [ROUTES.projects]: FaImages,
  [ROUTES.products]: FaShoppingBag,
  [ROUTES.services]: FaTools,
  [ROUTES.contact]: FaEnvelope,
};

const SOCIAL_ICONS = {
  facebook: FaFacebook,
  whatsapp: FaWhatsapp,
  phone: FaPhone,
};

function MobileSidebar({ isOpen, onToggle, onLanguageChange }) {
  const { t, i18n } = useTranslation();
  const [suppressTransition, setSuppressTransition] = useState(false);
  const isRtl = isRtlLanguage(i18n.language);
  const stateClass = isOpen ? "mobile-sidebar--open" : "mobile-sidebar--closed";
  const Chevron = isRtl ? FaChevronLeft : FaChevronRight;

  useEffect(() => {
    const onLangChange = () => {
      setSuppressTransition(true);
      onToggle(false);
    };
    i18n.on("languageChanged", onLangChange);
    return () => i18n.off("languageChanged", onLangChange);
  }, [i18n, onToggle]);

  useEffect(() => {
    if (!suppressTransition) return undefined;

    let innerId;
    const outerId = requestAnimationFrame(() => {
      innerId = requestAnimationFrame(() => setSuppressTransition(false));
    });

    return () => {
      cancelAnimationFrame(outerId);
      cancelAnimationFrame(innerId);
    };
  }, [suppressTransition, i18n.language]);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 1024px)");
    const closeOnDesktop = (event) => {
      if (event.matches) onToggle(false);
    };
    media.addEventListener("change", closeOnDesktop);
    if (media.matches) onToggle(false);
    return () => media.removeEventListener("change", closeOnDesktop);
  }, [onToggle]);

  useEffect(() => {
    if (isOpen) {
      lockBodyScroll();
    } else {
      unlockBodyScroll();
    }

    return () => {
      unlockBodyScroll();
    };
  }, [isOpen]);

  const closeSidebar = () => onToggle(false);

  const switchLanguage = (language) => {
    onLanguageChange?.();
    i18n.changeLanguage(language);
  };

  return (
    <aside
      aria-hidden={!isOpen}
      aria-label={t("Home")}
      className={`mobile-sidebar ${stateClass}${
        suppressTransition ? " mobile-sidebar--no-transition" : ""
      }`}
    >
      <div className="mobile-sidebar__brand">
        <div className="mobile-sidebar__brand-info">
          <img
            src={logoLight}
            alt="CAF Sports"
            className="mobile-sidebar__logo"
          />
          <div>
            <p className="mobile-sidebar__title">
              {t("CAF")} {t("SPORTS")}
            </p>
            <p className="mobile-sidebar__subtitle">{t("Home")}</p>
          </div>
        </div>
        <button
          type="button"
          className="mobile-sidebar__close"
          onClick={closeSidebar}
          aria-label={t("Close")}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <nav className="mobile-sidebar__nav">
        {NAV_LINKS.map(({ to, labelKey, mobileLabelKey }) => {
          const Icon = NAV_ICONS[to] || FaHome;
          return (
            <NavLink
              key={to}
              to={to}
              onClick={() => {
                closeSidebar();
                scrollToTop();
              }}
              className={({ isActive }) =>
                `navlink-mobile${isActive ? " active" : ""}`
              }
            >
              <span className="navlink-mobile__icon">
                <Icon aria-hidden="true" />
              </span>
              <span className="navlink-mobile__label">
                {t(mobileLabelKey || labelKey)}
              </span>
              <Chevron className="navlink-mobile__chevron" aria-hidden="true" />
            </NavLink>
          );
        })}
      </nav>

      <div className="mobile-sidebar__footer">
        <p className="mobile-sidebar__footer-label">{t("Contact Us")}</p>
        <div className="mobile-sidebar__socials">
          {SOCIAL_LINKS.map(({ id, href, external }) => {
            const Icon = SOCIAL_ICONS[id];
            return (
              <a
                key={id}
                href={href}
                target={external ? "_blank" : undefined}
                rel={external ? "noopener noreferrer" : undefined}
                aria-label={id}
                className="mobile-sidebar__social-link"
              >
                <Icon className="h-4 w-4" />
              </a>
            );
          })}
        </div>
        <div className="mobile-sidebar__lang-row">
          <button
            type="button"
            className={`mobile-sidebar__lang-btn enFont ${!isRtl ? "is-active" : ""}`}
            onClick={() => switchLanguage("en")}
          >
            English
          </button>
          <button
            type="button"
            className={`mobile-sidebar__lang-btn arFont ${isRtl ? "is-active" : ""}`}
            onClick={() => switchLanguage("ar")}
          >
            العربية
          </button>
        </div>
      </div>
    </aside>
  );
}

export default MobileSidebar;
